import { useRef, useState } from "react";
import { Camera, Sparkles } from "lucide-react";

export const shrinkImage = (file, max = 1600) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Photo illisible."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Photo illisible."));
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

/** Flow 18 (IA) — and Flow 23 input: a sentence and/or a photo, sent to the model. */
const CaptureCard = ({ title = "Mes travaux à préparer", placeholder, button = "Lire", testId = "capture-card", mobileType = false, time, onSubmit }) => {
  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const size = mobileType ? "text-[15px]" : "text-[13px]";

  const pick = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setImage(await shrinkImage(file));
    } catch (problem) {
      setError(problem.message);
    }
  };

  const send = async () => {
    if (!text.trim() && !image) return setError("Écris quelque chose ou ajoute une photo.");
    setBusy(true);
    setError("");
    try {
      await onSubmit({ text: text.trim(), image: image || null });
      setDone(true);
    } catch (problem) {
      setError(problem.message || "Envoi impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-[94%] sm:w-[88%] max-w-[23rem] self-start rounded-2xl rounded-tl-sm bg-white p-2.5 sm:p-3 shadow-sm" data-testid={testId}>
      <p className={`flex items-center gap-1.5 font-semibold text-[#122118] ${size}`}><Sparkles className="h-4 w-4 text-[#189346]" />{title}</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} disabled={done} rows={3} placeholder={placeholder}
        className={`mt-2 w-full resize-none rounded-xl border border-gray-200 px-2 py-1.5 outline-none focus:border-[#25D366] ${size}`} data-testid={`${testId}-text`} />
      {image && <img src={image} alt="Aperçu" className="mt-2 max-h-40 rounded-xl object-contain" data-testid={`${testId}-preview`} />}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
      {error && <p className="mt-2 text-xs text-[#9b3b3b]" data-testid={`${testId}-error`}>{error}</p>}
      {!done && (
        <div className="mt-2 flex gap-2">
          <button type="button" onClick={() => fileRef.current?.click()} className={`inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-2 font-semibold text-[#2d5a3d] ${size}`} data-testid={`${testId}-photo`}>
            <Camera className="h-4 w-4" />Photo
          </button>
          <button type="button" onClick={send} disabled={busy} className={`flex-1 rounded-full bg-[#25D366] px-3 py-2 font-semibold text-[#0a2914] disabled:opacity-50 ${size}`} data-testid={`${testId}-submit`}>
            {busy ? "Lecture…" : button}
          </button>
        </div>
      )}
      <div className="mt-1 text-right text-[10px] text-gray-500">{time}</div>
    </div>
  );
};

export default CaptureCard;
